// Single KPI tile for the Home overview row. Bigger and more decorative
// than the inline `<Stat>` it replaces — this is the first surface a
// returning operator sees, so the numbers earn the visual weight.
//
// Loading / zero / N/A semantics are explicit: callers pass `value` as
// `null` when the underlying query is still loading, in which case the
// tile renders a skeleton in the value slot. Zero is a valid value (no
// activity yet) and renders as "0", not as a placeholder.
//
// Layout adapts to viewport:
//   • mobile (<sm)        → vertical stack: icon on top, label/value/sublabel
//                           below at full card width. Avoids the "label
//                           overlaps icon" / "$0.00 truncates to $0…"
//                           bugs that horizontal layout produces in a
//                           ~150px-wide half-column card.
//   • desktop (sm+)       → horizontal: text on the left (label/value/
//                           sublabel stacked vertically), icon on the
//                           right. Saves ~40px of vertical space per row
//                           so the Home overview's 6 widgets fit a
//                           default-zoom viewport without scroll.

import type { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '../ui/card.js';
import { Skeleton } from '../ui/skeleton.js';
import { cn } from '../../lib/utils.js';

interface KpiCardProps {
  label: string;
  // `null` → loading state; `string` → render verbatim (caller handles
  // formatting + locale).
  value: string | null;
  sublabel?: string;
  icon: LucideIcon;
  className?: string;
}

export function KpiCard({ label, value, sublabel, icon: Icon, className }: KpiCardProps) {
  return (
    <Card className={cn('h-full', className)}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          {/* Icon — `sm:order-2` so it sits on the right in the horizontal
              desktop layout while remaining first in DOM order (which
              keeps the mobile stack with the icon visually on top). */}
          <span
            aria-hidden
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary sm:order-2 sm:h-10 sm:w-10"
          >
            <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
          </span>
          {/* Text column — `min-w-0` is required at sm+ so the value <p>
              can truncate inside a flex item rather than blowing the card
              out horizontally on long values. */}
          <div className="min-w-0 sm:flex-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {label}
            </p>
            {value === null ? (
              <Skeleton className="mt-1 h-8 w-24 sm:h-9" />
            ) : (
              <p className="mt-1 truncate text-2xl font-semibold tabular-nums sm:text-3xl">
                {value}
              </p>
            )}
            {sublabel && (
              <p className="mt-1 text-xs text-muted-foreground">{sublabel}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
